import { useCallback, useMemo } from 'react';

import { useDebouncedSearch } from '@spotify/confidence-core-react';
import { IdentityFragment } from '@spotify/confidence-graphql';

import { useCurrentIdentity } from '../useCurrentIdentity';
import { useCurrentUserGroups } from '../useCurrentUserGroups';
import { useIdentities } from '../useIdentities';
import { useRecentlySelectedIdentities } from '../useRecentlySelectedIdentities';

type IdentityPickerOptions = {
  currentIdentityOptions: IdentityFragment[];
  recentIdentityOptions: IdentityFragment[];
  groupIdentityOptions: IdentityFragment[];
  allIdentityOptions: IdentityFragment[];
  hasNextPage: boolean;
  isLoading: boolean;
  isLoadingNextPage: boolean;
  search: string;
  setSearch: (search: string) => void;
  addRecentIdentity: (name: string) => void;
  nextPage: () => void;
};

export const useIdentityPickerOptions = (): IdentityPickerOptions => {
  const { query, search, setSearch } = useDebouncedSearch();
  const { currentIdentity } = useCurrentIdentity();
  const { identities: userGroups = [], isLoading: userGroupsLoading } =
    useCurrentUserGroups();
  const {
    recentIdentities,
    addRecentIdentity,
    isLoading: recentIdentitiesLoading,
  } = useRecentlySelectedIdentities();
  const {
    identities = [],
    nextPage: fetchNextPage,
    hasNextPage,
    isLoading,
    isLoadingNextPage,
  } = useIdentities({
    filter: query ? `displayName:${query}*` : '',
  });
  const {
    currentIdentityOptions,
    recentIdentityOptions,
    groupIdentityOptions,
    allIdentityOptions,
  } = useMemo(() => {
    const identitiesByName = new Map<string, IdentityFragment>();
    const currentOptions: IdentityFragment[] = [];
    const recentOptions: IdentityFragment[] = [];
    const groupOptions: IdentityFragment[] = [];
    const allOptions: IdentityFragment[] = [];

    const addIdentity = (
      identity: IdentityFragment,
      options: IdentityFragment[],
    ): void => {
      if (!identitiesByName.has(identity.name)) {
        identitiesByName.set(identity.name, identity);
        options.push(identity);
      }
    };

    if (!query && currentIdentity) {
      addIdentity(currentIdentity, currentOptions);
      recentIdentities.forEach(identity => {
        addIdentity(identity, recentOptions);
      });
      userGroups.forEach(identity => {
        addIdentity(identity, groupOptions);
      });
    }

    identities.forEach(identity => {
      addIdentity(identity, allOptions);
    });

    return {
      currentIdentityOptions: currentOptions,
      recentIdentityOptions: recentOptions,
      groupIdentityOptions: groupOptions,
      allIdentityOptions: allOptions,
    };
  }, [currentIdentity, identities, query, recentIdentities, userGroups]);
  const nextPage = useCallback((): void => {
    void fetchNextPage();
  }, [fetchNextPage]);

  return {
    currentIdentityOptions,
    recentIdentityOptions,
    groupIdentityOptions,
    allIdentityOptions,
    hasNextPage,
    isLoading: isLoading || recentIdentitiesLoading || userGroupsLoading,
    isLoadingNextPage,
    search,
    setSearch,
    addRecentIdentity,
    nextPage,
  };
};
